import { useState, useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  CreditCard,
  BellRing,
  FileBarChart,
  ClipboardList,
  HelpCircle,
  X,
  CalendarDays,
  Link as LinkIcon,
  ShieldCheck,
  Mail,
  ChevronLeft,
  ChevronRight,
  Shield,
  AlertTriangle,
  Globe,
  FileSearch,
  HeartPulse,
  ClipboardCheck,
  TrendingDown,
} from 'lucide-react';
import { ScrollArea } from '../ui/scroll-area';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { cn } from '../../lib/utils';
import { APP_VERSION } from '../../version';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

const navSections: NavSection[] = [
  {
    title: 'Main',
    items: [
      { name: 'Dashboard', href: '/', icon: LayoutDashboard },
      { name: 'Clients', href: '/clients', icon: Users },
      { name: 'Tenant Matcher', href: '/tenant-matcher', icon: LinkIcon },
    ],
  },
  {
    title: 'Licensing',
    items: [
      { name: 'Subscriptions', href: '/subscriptions', icon: CreditCard },
      { name: 'Renewal Calendar', href: '/calendar', icon: CalendarDays },
      { name: 'Margin Audit', href: '/margin-audit', icon: TrendingDown },
    ],
  },
  {
    title: 'Monitoring',
    items: [
      { name: 'Alerts', href: '/alerts', icon: BellRing },
      { name: 'Reports', href: '/reports', icon: FileBarChart },
      { name: 'Security Reports', href: '/scheduled-reports', icon: ShieldCheck },
      { name: 'Audit Log', href: '/audit-logs', icon: ClipboardList },
      { name: 'Message Trace', href: '/message-trace', icon: Mail },
      { name: 'Email Health', href: '/email-health', icon: HeartPulse },
      { name: 'DKIM Migration', href: '/email-health/dkim-migration', icon: ShieldCheck },
    ],
  },
  {
    title: 'Security',
    items: [
      { name: 'SOC Dashboard', href: '/security', icon: Shield },
      { name: 'Security Alerts', href: '/security/alerts', icon: AlertTriangle },
      { name: 'Investigations', href: '/security/investigations', icon: FileSearch },
      { name: 'Threat Map', href: '/security/map', icon: Globe },
      { name: 'Compliance', href: '/compliance', icon: ClipboardCheck },
    ],
  },
  {
    title: 'Support',
    items: [
      { name: 'Help', href: '/help', icon: HelpCircle },
    ],
  },
];

export default function Sidebar({ isOpen, onClose, collapsed, onCollapsedChange }: SidebarProps) {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const sidebarContent = (showLabels: boolean) => (
    <div className="flex flex-col h-full">
      <ScrollArea className="flex-1">
        <nav className={cn("space-y-4 py-4", showLabels ? "px-3" : "px-2")}>
          {navSections.map((section) => (
            <div key={section.title}>
              {showLabels && (
                <h3 className="mb-1 px-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                  {section.title}
                </h3>
              )}
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const navLink = (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      end={item.href === '/'}
                      onClick={onClose}
                      className={({ isActive }) =>
                        cn(
                          "group relative flex items-center rounded-lg text-sm font-medium transition-all duration-200",
                          showLabels ? "gap-3 px-3 py-2" : "justify-center p-2.5",
                          isActive
                            ? "bg-primary-600/10 text-primary-600 dark:bg-primary-500/15 dark:text-primary-400"
                            : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {isActive && (
                            <div className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-[3px] rounded-r-full bg-primary-600 dark:bg-primary-400" />
                          )}
                          <item.icon className={cn("shrink-0", showLabels ? "h-[18px] w-[18px]" : "h-5 w-5")} />
                          {showLabels && <span>{item.name}</span>}
                        </>
                      )}
                    </NavLink>
                  );

                  if (!showLabels) {
                    return (
                      <Tooltip key={item.name} delayDuration={0}>
                        <TooltipTrigger asChild>
                          {navLink}
                        </TooltipTrigger>
                        <TooltipContent side="right" className="font-medium">
                          {item.name}
                        </TooltipContent>
                      </Tooltip>
                    );
                  }

                  return navLink;
                })}
              </div>
            </div>
          ))}
        </nav>
      </ScrollArea>

      {/* Footer */}
      <div className={cn(
        "border-t border-border/50 p-3",
        !showLabels && "flex justify-center"
      )}>
        {showLabels ? (
          <div className="flex items-center justify-between">
            <Link
              to="/changelog"
              onClick={onClose}
              className="inline-flex items-center gap-2 text-xs group"
            >
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-primary-600/10 text-primary-600 dark:bg-primary-500/15 dark:text-primary-400 font-medium text-[11px]">
                v{APP_VERSION}
              </span>
              <span className="text-muted-foreground group-hover:text-foreground transition-colors">
                Changelog
              </span>
            </Link>
            <button
              onClick={() => onCollapsedChange(true)}
              className="p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
              title="Collapse sidebar"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => onCollapsedChange(false)}
            className="p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
            title="Expand sidebar"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile sidebar backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Mobile sidebar */}
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-[280px] transform bg-card border-r border-border shadow-xl transition-transform duration-300 ease-in-out lg:hidden",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex h-16 items-center justify-between px-4 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg gradient-primary shadow-sm">
              <span className="text-white font-bold text-sm">P8</span>
            </div>
            <span className="text-lg font-semibold text-foreground">
              Pax8 Licensing
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {sidebarContent(true)}
      </div>

      {/* Desktop sidebar */}
      <div
        className={cn(
          "hidden lg:fixed lg:inset-y-0 lg:z-20 lg:flex lg:flex-col border-r border-border/50 bg-card/50 backdrop-blur-xl",
          isMounted ? "transition-all duration-300 ease-in-out" : "",
          collapsed ? "lg:w-[70px]" : "lg:w-[280px]"
        )}
      >
        <div className="flex min-h-0 flex-1 flex-col pt-16">
          {sidebarContent(!collapsed)}
        </div>
      </div>
    </>
  );
}
